import { create } from "zustand";
import type { AppliedDecision, Proposal, ToolActivity } from "./assistantApi";

export interface ChatMessage {
  id: number;
  role: "user" | "assistant";
  text: string;
  tools: ToolActivity[];
  proposalIds: string[];
  error?: string;
  notices: string[];
  streaming: boolean;
  /** Tool calls happened since the last text delta; the next delta starts a new paragraph. */
  breakBeforeText?: boolean;
}

export type ProposalStatus = "pending" | "applying" | "applied" | "rejected" | "failed";

export interface TrackedProposal {
  proposal: Proposal;
  status: ProposalStatus;
  error?: string;
  messageId: number;
}

interface ChatState {
  projectId: string | null;
  conversationId: string | null;
  model: string | null;
  messages: ChatMessage[];
  proposals: Record<string, TrackedProposal>;
  decisions: AppliedDecision[];
  busy: boolean;
  draft: string;
  setDraft: (d: string) => void;
  ensureProject: (projectId: string | null) => void;
  reset: () => void;
  beginTurn: (userText: string) => number;
  appendText: (id: number, delta: string) => void;
  addTool: (id: number, tool: ToolActivity) => void;
  endTool: (id: number, toolId: string, ok: boolean, summary: string) => void;
  addProposal: (id: number, p: Proposal) => void;
  addNotice: (id: number, text: string) => void;
  failTurn: (id: number, error: string) => void;
  endTurn: (id: number, conversationId: string | null, model?: string | null) => void;
  setProposalStatus: (pid: string, status: ProposalStatus, error?: string) => void;
  takeDecisions: () => AppliedDecision[];
}

let seq = 1;

export const useChat = create<ChatState>()((set, get) => ({
  projectId: null,
  conversationId: null,
  model: null,
  messages: [],
  proposals: {},
  decisions: [],
  busy: false,
  draft: "",
  setDraft: (d) => set({ draft: d }),
  ensureProject: (projectId) => {
    if (get().projectId === projectId) return;
    set({ projectId, conversationId: null, messages: [], proposals: {}, decisions: [], busy: false });
  },
  reset: () => set({ conversationId: null, messages: [], proposals: {}, decisions: [], busy: false }),
  beginTurn: (userText) => {
    const user: ChatMessage = { id: seq++, role: "user", text: userText, tools: [], proposalIds: [], notices: [], streaming: false };
    const assistant: ChatMessage = { id: seq++, role: "assistant", text: "", tools: [], proposalIds: [], notices: [], streaming: true };
    set({ messages: [...get().messages, user, assistant], busy: true });
    return assistant.id;
  },
  appendText: (id, delta) =>
    set({
      messages: get().messages.map((m) => {
        if (m.id !== id) return m;
        const needsBreak = m.breakBeforeText && m.text && !/\n\s*$/.test(m.text) && !/^\s*\n/.test(delta);
        return { ...m, text: m.text + (needsBreak ? "\n\n" : "") + delta, breakBeforeText: false };
      }),
    }),
  addTool: (id, tool) =>
    set({ messages: get().messages.map((m) => (m.id === id ? { ...m, tools: [...m.tools, tool], breakBeforeText: true } : m)) }),
  endTool: (id, toolId, ok, summary) =>
    set({
      messages: get().messages.map((m) =>
        m.id === id ? { ...m, tools: m.tools.map((t) => (t.id === toolId ? { ...t, ok, summary, done: true } : t)) } : m,
      ),
    }),
  addProposal: (id, p) => {
    const proposals = { ...get().proposals };
    // a newer proposal for the same target supersedes the pending one
    for (const [pid, tp] of Object.entries(proposals)) {
      const same =
        tp.proposal.kind === p.kind &&
        ((p.kind === "file" && tp.proposal.kind === "file" && tp.proposal.path === p.path) ||
          (p.kind === "frame_parameter" && tp.proposal.kind === "frame_parameter" && tp.proposal.mnemonic === p.mnemonic));
      if (same && tp.status === "pending") delete proposals[pid];
    }
    proposals[p.id] = { proposal: p, status: "pending", messageId: id };
    set({
      proposals,
      messages: get().messages.map((m) => (m.id === id ? { ...m, proposalIds: [...m.proposalIds, p.id] } : m)),
    });
  },
  addNotice: (id, text) => set({ messages: get().messages.map((m) => (m.id === id ? { ...m, notices: [...m.notices, text] } : m)) }),
  failTurn: (id, error) =>
    set({ busy: false, messages: get().messages.map((m) => (m.id === id ? { ...m, error, streaming: false } : m)) }),
  endTurn: (id, conversationId, model) =>
    set({
      busy: false,
      conversationId: conversationId ?? get().conversationId,
      model: model ?? get().model,
      messages: get().messages.map((m) => (m.id === id ? { ...m, streaming: false } : m)),
    }),
  setProposalStatus: (pid, status, error) => {
    const tp = get().proposals[pid];
    if (!tp) return;
    const decisions = [...get().decisions];
    if (status === "applied" || status === "rejected") {
      decisions.push({
        id: pid,
        applied: status === "applied",
        path: tp.proposal.kind === "file" ? tp.proposal.path : null,
        mnemonic: tp.proposal.kind === "frame_parameter" ? tp.proposal.mnemonic : null,
      });
    }
    set({ proposals: { ...get().proposals, [pid]: { ...tp, status, error } }, decisions });
  },
  takeDecisions: () => {
    const d = get().decisions;
    set({ decisions: [] });
    return d;
  },
}));
